import { create } from 'zustand';
import { toast } from 'react-hot-toast';
import { logger } from '@/features/help/utils/logger';
import api from '@/features/api/axios';

interface NotificationSettings {
  notificationEmails: string[];
  notifyCrmOwner: boolean;
  allowAdditionalSignerDocUpload: boolean;
  brokerNotificationDelaySeconds: number;
  applicantReminderEnabled: boolean;
  applicantReminderCadenceHours: number;
  applicantReminderMaxReminders: number;
}

interface BankStatementAnalysisSettings {
  bankStatementAnalysisEnabled: boolean;
  bankStatementAnalysisLastFourMonths: boolean;
}

interface SettingsState {
  // Notification settings
  notificationEmails: string[];
  notifyCrmOwner: boolean;
  allowAdditionalSignerDocUpload: boolean;
  brokerNotificationDelaySeconds: number;
  applicantReminderEnabled: boolean;
  applicantReminderCadenceHours: number;
  applicantReminderMaxReminders: number;
  bankStatementAnalysisEnabled: boolean;
  bankStatementAnalysisLastFourMonths: boolean;

  // UI States
  isLoading: boolean;
  isSaving: boolean;
  isSavingDocUpload: boolean;
  isSavingBankStatementAnalysis: boolean;
  error: string | null;

  // Actions
  fetchSettings: () => Promise<void>;
  setAllowAdditionalSignerDocUpload: (allow: boolean) => void;
  setBankStatementAnalysisEnabled: (enabled: boolean) => void;
  setBankStatementAnalysisLastFourMonths: (enabled: boolean) => void;
  updateSettings: (settings: Partial<NotificationSettings>) => Promise<boolean>;
  updateAllowAdditionalSignerDocUpload: (value: boolean) => Promise<boolean>;
  updateBankStatementAnalysisSettings: (settings: BankStatementAnalysisSettings) => Promise<boolean>;
}

/**
 * Store for user settings including notification preferences
 */
export const useSettingsStore = create<SettingsState>((set) => ({
  // Initial state
  notificationEmails: [],
  notifyCrmOwner: false,
  allowAdditionalSignerDocUpload: false,
  brokerNotificationDelaySeconds: 180,
  applicantReminderEnabled: false,
  applicantReminderCadenceHours: 24,
  applicantReminderMaxReminders: 3,
  bankStatementAnalysisEnabled: false,
  bankStatementAnalysisLastFourMonths: false,
  isLoading: false,
  isSaving: false,
  isSavingDocUpload: false,
  isSavingBankStatementAnalysis: false,
  error: null,

  /**
   * Fetches user settings from the API
   */
  fetchSettings: async () => {
    try {
      set({ isLoading: true, error: null });
      logger.info('SettingsFlow', 'Fetching user settings');

      const response = await api.get('/api/email/notifications/settings');
      let analysisResponse: { data: Partial<BankStatementAnalysisSettings> } | null = null;
      try {
        analysisResponse = await api.get('/api/settings/bank-statement-analysis');
      } catch (analysisError) {
        logger.error('SettingsFlow', 'Error fetching bank statement analysis settings', {
          error: analysisError,
        });
      }

      logger.info('SettingsFlow', 'Settings fetched successfully', { data: response.data });

      set({
        notificationEmails: response.data.notificationEmails || [],
        notifyCrmOwner: response.data.notifyCrmOwner || false,
        allowAdditionalSignerDocUpload: response.data.allowAdditionalSignerDocUpload || false,
        brokerNotificationDelaySeconds: response.data.brokerNotificationDelaySeconds || 180,
        applicantReminderEnabled: response.data.applicantReminderEnabled || false,
        applicantReminderCadenceHours: response.data.applicantReminderCadenceHours || 24,
        applicantReminderMaxReminders: response.data.applicantReminderMaxReminders || 3,
        bankStatementAnalysisEnabled: analysisResponse?.data.bankStatementAnalysisEnabled || false,
        bankStatementAnalysisLastFourMonths:
          analysisResponse?.data.bankStatementAnalysisLastFourMonths || false,
        isLoading: false,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      logger.error('SettingsFlow', 'Error fetching settings', { error });
      set({ error: errorMessage, isLoading: false });
      toast.error('Failed to load settings');
    }
  },

  /**
   * Sets whether additional signers can upload documents
   */
  setAllowAdditionalSignerDocUpload: (allow) => {
    set({ allowAdditionalSignerDocUpload: allow });
  },

  /**
   * Sets bank statement analysis enabled in the store
   */
  setBankStatementAnalysisEnabled: (enabled) => {
    set({ bankStatementAnalysisEnabled: enabled });
  },

  /**
   * Sets bank statement analysis "last 4 months" toggle in the store
   */
  setBankStatementAnalysisLastFourMonths: (enabled) => {
    set({ bankStatementAnalysisLastFourMonths: enabled });
  },

  /**
   * Updates user settings via the API
   */
  updateSettings: async (settings) => {
    try {
      set({ isSaving: true, error: null });
      logger.info('SettingsFlow', 'Updating user settings', { settings });

      const response = await api.put('/api/email/notifications/settings', settings);

      logger.info('SettingsFlow', 'Settings updated successfully', { data: response.data });

      set({
        notificationEmails: response.data.notificationEmails || [],
        notifyCrmOwner: response.data.notifyCrmOwner || false,
        allowAdditionalSignerDocUpload: response.data.allowAdditionalSignerDocUpload || false,
        brokerNotificationDelaySeconds: response.data.brokerNotificationDelaySeconds || 180,
        applicantReminderEnabled: response.data.applicantReminderEnabled || false,
        applicantReminderCadenceHours: response.data.applicantReminderCadenceHours || 24,
        applicantReminderMaxReminders: response.data.applicantReminderMaxReminders || 3,
        isSaving: false,
      });

      toast.success('Settings saved successfully');
      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      logger.error('SettingsFlow', 'Error updating settings', { error });
      set({ error: errorMessage, isSaving: false });
      toast.error('Failed to save settings');
      return false;
    }
  },

  /**
   * Updates the document upload setting via dedicated endpoint
   * This is decoupled from other notification settings to avoid unnecessary re-renders
   */
  updateAllowAdditionalSignerDocUpload: async (value) => {
    try {
      set({ isSavingDocUpload: true, error: null });
      logger.info('SettingsFlow', 'Updating document upload setting', { value });

      const response = await api.put('/api/settings/allow-additional-signer-doc-upload', {
        allowAdditionalSignerDocUpload: value,
      });

      logger.info('SettingsFlow', 'Document upload setting updated successfully', { data: response.data });

      set({
        allowAdditionalSignerDocUpload: response.data.allowAdditionalSignerDocUpload,
        isSavingDocUpload: false,
      });

      toast.success('Setting saved successfully');
      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      logger.error('SettingsFlow', 'Error updating document upload setting', { error });
      set({ error: errorMessage, isSavingDocUpload: false });
      toast.error('Failed to save setting');
      return false;
    }
  },

  /**
   * Updates bank statement analysis settings via dedicated endpoint
   */
  updateBankStatementAnalysisSettings: async (settings) => {
    try {
      set({ isSavingBankStatementAnalysis: true, error: null });
      logger.info('SettingsFlow', 'Updating bank statement analysis settings', { settings });

      const response = await api.put('/api/settings/bank-statement-analysis', settings);

      logger.info('SettingsFlow', 'Bank statement analysis settings updated successfully', {
        data: response.data,
      });

      set({
        bankStatementAnalysisEnabled: response.data.bankStatementAnalysisEnabled || false,
        bankStatementAnalysisLastFourMonths: response.data.bankStatementAnalysisLastFourMonths || false,
        isSavingBankStatementAnalysis: false,
      });

      toast.success('Setting saved successfully');
      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      logger.error('SettingsFlow', 'Error updating bank statement analysis settings', { error });
      set({ error: errorMessage, isSavingBankStatementAnalysis: false });
      toast.error('Failed to save setting');
      return false;
    }
  },
}));
